<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import { VDataTableServer } from 'vuetify/components';

import { useAppState } from '@/src/store/ocg-app-state';
import { session, supabase } from '@/src/supabase';

const props = defineProps({
  studyInstanceUid: {
    type: String,
    required: true,
  },
  userUid: {
    type: String,
  },
});

const emit = defineEmits<{
  clicked: [item: Item];
  removeClicked: [item: Item];
}>();

const SYSTEM_USER_ID = '7fd2b0e5-17b4-433c-9f45-93a5243d452e';

const appState = useAppState();

export interface Item {
  id: string;
  title: string;

  study_instance_uid?: string;
  user: string;
  userMeta?: { card?: { fn?: string; name?: string; email?: string } };
  userName?: string;
  org: string;
  orgMeta?: { person?: { card?: { fn?: string; name?: string } } };
  orgName?: string;
  isOwner?: boolean;
  isSystem?: boolean;

  created_at: string;
}

const items = ref<Item[]>([]);
const isLoading = ref(true);

const headers = [
  { title: 'Name', key: 'title', value: 'title', sortable: true },
  { title: 'Created at', key: 'created_at', value: 'created_at', sortable: true },
  { title: 'User', key: 'userName', value: 'userName', align: 'start', sortable: true },
  {
    title: 'Organization',
    key: 'orgName',
    value: 'orgName',
    align: 'start',
    sortable: true,
  },
  { title: 'Actions', key: 'actions', align: 'end', sortable: false },
] as const;

function resolveUserName(row: any): string {
  // 1. If it's system user:
  if (row.user === SYSTEM_USER_ID) {
    return 'System Auto';
  }

  // 2. If it's the current user:
  if (session.value?.user?.id && row.user === session.value.user.id) {
    const currentCard = appState.currentUser;
    const meta = session.value.user.user_metadata;
    const name = currentCard?.fn || meta?.fn || meta?.full_name || meta?.name || session.value.user.email;
    if (name) return name;
  }

  // 3. Direct PostgREST extractions from state jsonb
  if (row.created_by_fn && String(row.created_by_fn).trim()) {
    return String(row.created_by_fn).trim();
  }
  if (row.created_by_name && String(row.created_by_name).trim()) {
    return String(row.created_by_name).trim();
  }
  if (row.created_by_email && String(row.created_by_email).trim()) {
    return String(row.created_by_email).trim();
  }

  // 4. From created_by object in state
  const createdBy = row.created_by || row.state?.createdBy;
  if (createdBy) {
    const card = createdBy.card;
    if (typeof card === 'object' && card !== null) {
      if (card.fn && String(card.fn).trim()) return String(card.fn).trim();
      if (card.name && String(card.name).trim()) return String(card.name).trim();
      if (card.email && String(card.email).trim()) return String(card.email).trim();
    }
    else if (typeof card === 'string' && card.trim()) {
      return card.trim();
    }
    if (createdBy.fn && String(createdBy.fn).trim()) return String(createdBy.fn).trim();
    if (createdBy.name && String(createdBy.name).trim()) return String(createdBy.name).trim();
    if (createdBy.email && String(createdBy.email).trim()) return String(createdBy.email).trim();
  }

  // 5. From person table join (userMeta)
  const userCard = row.userMeta?.card;
  if (userCard) {
    if (typeof userCard === 'object' && userCard !== null) {
      if (userCard.fn && String(userCard.fn).trim()) return String(userCard.fn).trim();
      if (userCard.name && String(userCard.name).trim()) return String(userCard.name).trim();
      if (userCard.email && String(userCard.email).trim()) return String(userCard.email).trim();
    }
    else if (typeof userCard === 'string' && userCard.trim()) {
      return userCard.trim();
    }
  }

  // 6. User ID fallback if available
  if (row.user) {
    return `User (${row.user.slice(0, 8)})`;
  }

  return 'Unknown User';
}

function resolveOrgName(row: any): string {
  const orgPersonCard = row.orgMeta?.person?.card;
  if (orgPersonCard?.fn) return orgPersonCard.fn;
  if (orgPersonCard?.name) return orgPersonCard.name;
  return 'Default Organization';
}

async function reload() {
  try {
    isLoading.value = true;
    const query = supabase.from('analysis_state').select(
      `id,
      study_instance_uid,
      user,
      userMeta:person!analysis_state_user_fkey(card),
      org,
      orgMeta:org!analysis_state_org_fkey(
        person(card)
      ),
      title:state->>title,
      created_by:state->createdBy,
      created_by_fn:state->createdBy->card->>fn,
      created_by_email:state->createdBy->card->>email,
      created_by_name:state->createdBy->>name,
      created_at
      `,
    );
    if (props.studyInstanceUid) {
      query.eq('study_instance_uid', props.studyInstanceUid);
    }
    if (props.userUid) {
      query.eq('user', props.userUid);
    }
    query.order('created_at', { ascending: false });
    const { data, error } = await query;
    if (error) {
      throw error;
    }

    const rows = data || [];

    // Check if any users need additional person profile resolution
    const unresolvedUserIds = [
      ...new Set(
        rows
          .filter((r: any) => !r.created_by_fn && !r.userMeta?.card?.fn && r.user !== SYSTEM_USER_ID && r.user !== session.value?.user?.id)
          .map((r: any) => r.user)
          .filter(Boolean),
      ),
    ];

    let personLookup = new Map<string, any>();
    if (unresolvedUserIds.length > 0) {
      try {
        const { data: persons } = await supabase
          .from('person')
          .select('id, card')
          .in('id', unresolvedUserIds);
        if (persons && persons.length > 0) {
          personLookup = new Map(persons.map(p => [p.id, p.card]));
        }
      }
      catch {
        // Silently continue if person query is restricted
      }
    }

    items.value = rows.map((row: any) => {
      if (personLookup.has(row.user) && !row.userMeta) {
        row.userMeta = { card: personLookup.get(row.user) };
      }
      const isOwner = !!(session.value?.user?.id && row.user === session.value.user.id);
      const isSystem = row.user === SYSTEM_USER_ID;
      const userName = resolveUserName(row);
      const orgName = resolveOrgName(row);
      const title = row.title || 'Untitled Save';

      return {
        ...row,
        title,
        userName,
        orgName,
        isOwner,
        isSystem,
      };
    });
  }
  catch (error) {
    console.error('Error fetching items:', error);
  }
  finally {
    isLoading.value = false;
  }
}

watch(
  [() => props.studyInstanceUid, () => props.userUid],
  () => {
    void reload();
  },
);

onMounted(() => {
  void reload();
});

// tv: wow, that's some type magic...
type Row = Parameters<VDataTableServer['$slots']['item']>[0];

function handleRowClick(_event: MouseEvent, row: Row) {
  emit('clicked', row.item);
}

function handleRemoveClicked(item: Item) {
  emit('removeClicked', item);
}

defineExpose({
  reload,
});
</script>

<template>
  <VDataTableServer
    :headers="headers"
    :items="items"
    :items-length="items.length"
    :items-per-page="5"
    :loading="isLoading"
    class="analysis-state-table"
    @click:row="handleRowClick"
    @update:options="reload"
  >
    <template #[`item.title`]="{ item }">
      <span class="font-weight-medium">{{ item.title || 'Untitled Save' }}</span>
    </template>
    <template #[`item.created_at`]="{ item }">
      <span>{{ new Date(item.created_at).toLocaleString() }}</span>
    </template>
    <template #[`item.userName`]="{ item }">
      <div class="d-flex align-center">
        <span>{{ item.userName }}</span>
        <v-chip
          v-if="item.isOwner"
          size="x-small"
          color="primary"
          variant="tonal"
          class="ml-2 font-weight-bold"
        >
          You
        </v-chip>
        <v-chip
          v-else-if="item.isSystem"
          size="x-small"
          color="info"
          variant="tonal"
          class="ml-2 font-weight-bold"
        >
          System
        </v-chip>
      </div>
    </template>
    <template #[`item.orgName`]="{ item }">
      <span>{{ item.orgName }}</span>
    </template>
    <template #[`item.actions`]="{ item }">
      <v-tooltip
        :text="item.isOwner ? 'Delete analysis state' : 'Cannot delete another user\'s save'"
        location="top"
      >
        <template #activator="{ props: tooltipProps }">
          <v-icon
            v-bind="tooltipProps"
            color="medium-emphasis"
            icon="mdi-delete"
            size="small"
            :disabled="!item.isOwner"
            @click.stop="handleRemoveClicked(item)"
          />
        </template>
      </v-tooltip>
    </template>
  </VDataTableServer>
</template>

<style scoped>
:deep(.v-data-table__tr) {
  cursor: pointer;
}
:deep(.v-data-table__tr:hover) {
  background-color: rgba(var(--v-theme-primary), 0.05) !important;
}
</style>
